import { useEffect, useState, useCallback, useMemo } from 'react';
import {
  api,
  type RuntimeNode,
  type RuntimeNodesResponse,
  type RuntimeAccountSession,
  type RuntimeNodeStatus,
} from '../lib/api';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { PageHeader } from '@/components/layout/PageHeader';
import { cn } from '@/lib/utils';
import {
  Server,
  RefreshCw,
  Activity,
  Cpu,
  AlertTriangle,
} from 'lucide-react';

const REFRESH_INTERVAL_MS = 30_000;

const STATUS_BADGE: Record<RuntimeNodeStatus, 'success' | 'warning' | 'danger'> = {
  online: 'success',
  draining: 'warning',
  offline: 'danger',
};

const STATUS_DOT: Record<RuntimeNodeStatus, string> = {
  online: 'bg-raios-success',
  draining: 'bg-raios-warning',
  offline: 'bg-raios-danger',
};

/* ─── Helpers ──────────────────────────────────────────────────────────────── */

function relativeTime(iso: string | null): string {
  if (!iso) return 'nunca';
  const d = new Date(iso).getTime();
  if (Number.isNaN(d)) return 'desconhecido';
  const diffMs = Date.now() - d;
  const sec = Math.floor(diffMs / 1000);
  if (sec < 10) return 'agora';
  if (sec < 60) return `${sec}s atrás`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}min atrás`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h atrás`;
  const day = Math.floor(hr / 24);
  return `${day}d atrás`;
}

/* ─── Page ─────────────────────────────────────────────────────────────────── */

export function RuntimeNodesPage() {
  const [data, setData] = useState<RuntimeNodesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [usingMock, setUsingMock] = useState(false);

  const load = useCallback(async (opts?: { silent?: boolean }) => {
    if (opts?.silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);
    try {
      const res = await api.listRuntimeNodes();
      setData(res);
      setUsingMock(false);
      setLastUpdated(new Date());
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro ao carregar nós';
      // Fallback to mock so the page is still useful while backend is not wired
      if (msg.includes('404') || msg.toLowerCase().includes('not found')) {
        setData(buildMockData());
        setUsingMock(true);
        setLastUpdated(new Date());
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const t = setInterval(() => {
      void load({ silent: true });
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(t);
  }, [load]);

  const nodes = useMemo(() => data?.nodes ?? [], [data]);
  const sessions: RuntimeAccountSession[] = useMemo(
    () => data?.llm_sessions_by_account ?? [],
    [data]
  );
  const pressure = useMemo(() => data?.memory_pressure_events ?? [], [data]);

  const fleetStats = useMemo(() => {
    const online = nodes.filter((n) => n.status === 'online').length;
    const draining = nodes.filter((n) => n.status === 'draining').length;
    const offline = nodes.filter((n) => n.status === 'offline').length;
    const totalWorkers = nodes.reduce((sum, n) => sum + (n.max_workers ?? 0), 0);
    const totalInProgress = nodes.reduce(
      (sum, n) => sum + (n.tasks_in_progress ?? 0),
      0
    );
    return { online, draining, offline, totalWorkers, totalInProgress };
  }, [nodes]);

  return (
    <div>
      <PageHeader
        title="Runtime Nodes"
        description="Monitoramento do fleet de execução"
        actions={
          <div className="flex items-center gap-3">
            {lastUpdated && (
              <span className="text-xs text-raios-muted hidden sm:inline">
                Atualizado {relativeTime(lastUpdated.toISOString())}
              </span>
            )}
            <Button
              variant="default"
              size="sm"
              onClick={() => void load()}
              loading={refreshing}
            >
              <RefreshCw className="w-4 h-4" />
              Atualizar
            </Button>
          </div>
        }
        mock={usingMock}
        className="mb-6"
      />

      {error && (
        <Card className="bg-raios-danger/10 px-4 py-3 mb-4">
          <p className="text-raios-danger-text text-sm">{error}</p>
        </Card>
      )}

      {loading ? (
        <div className="space-y-3">
          <Skeleton className="h-24 rounded-xl" />
          <Skeleton className="h-48 rounded-xl" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Fleet summary */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <SummaryCard label="Online" value={fleetStats.online} accent="success" />
            <SummaryCard label="Draining" value={fleetStats.draining} accent="warning" />
            <SummaryCard label="Offline" value={fleetStats.offline} accent="danger" />
            <SummaryCard label="Workers" value={fleetStats.totalWorkers} accent="info" />
            <SummaryCard label="In Progress" value={fleetStats.totalInProgress} accent="brand" />
          </div>

          {/* Nodes list */}
          <section>
            <h2 className="text-sm font-semibold text-raios-text-secondary mb-3 flex items-center gap-2">
              <Server className="w-4 h-4" />
              Nós do Fleet
            </h2>
            {nodes.length === 0 ? (
              <EmptyState
                icon={Server}
                title="Nenhum nó registrado"
                description="Nenhum runtime node foi reportado ainda."
              />
            ) : (
              <div className="space-y-3">
                {nodes.map((n) => (
                  <NodeCard key={n.id} node={n} />
                ))}
              </div>
            )}
          </section>

          {/* LLM sessions by account */}
          <section>
            <h2 className="text-sm font-semibold text-raios-text-secondary mb-3 flex items-center gap-2">
              <Activity className="w-4 h-4" />
              Sessões LLM por Account
            </h2>
            {sessions.length === 0 ? (
              <Card className="p-6 text-center">
                <p className="text-xs text-raios-muted">Sem sessões ativas.</p>
              </Card>
            ) : (
              <SessionsChart sessions={sessions} />
            )}
          </section>

          {/* Memory pressure events */}
          <section>
            <h2 className="text-sm font-semibold text-raios-text-secondary mb-3 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Memory Pressure (últimos eventos)
            </h2>
            {pressure.length === 0 ? (
              <Card className="p-6 text-center">
                <p className="text-xs text-raios-muted">Sem eventos recentes.</p>
              </Card>
            ) : (
              <Card className="p-0 overflow-hidden">
                <ul className="divide-y divide-raios-border">
                  {pressure.map((e) => (
                    <li key={e.id} className="px-4 py-3 flex items-center justify-between">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <Badge variant={e.level === 'critical' ? 'danger' : 'warning'}>
                            {e.level}
                          </Badge>
                          {e.hostname && (
                            <span className="text-xs font-mono text-raios-text-secondary">
                              {e.hostname}
                            </span>
                          )}
                        </div>
                        {e.message && (
                          <p className="text-xs text-raios-muted truncate">{e.message}</p>
                        )}
                      </div>
                      <span className="text-xs text-raios-muted shrink-0 ml-3">
                        {relativeTime(e.occurred_at)}
                      </span>
                    </li>
                  ))}
                </ul>
              </Card>
            )}
          </section>
        </div>
      )}
    </div>
  );
}

/* ─── Sub-components ───────────────────────────────────────────────────────── */

function SummaryCard({
  label,
  value,
  accent,
}: {
  label: string;
  value: number;
  accent: 'success' | 'warning' | 'danger' | 'info' | 'brand';
}) {
  const colorClass = {
    success: 'text-raios-success-text',
    warning: 'text-raios-warning-text',
    danger: 'text-raios-danger-text',
    info: 'text-raios-info-text',
    brand: 'text-raios-accent-text',
  }[accent];

  return (
    <Card className="p-4">
      <p className="text-[10px] font-semibold uppercase tracking-widest text-raios-muted mb-1">
        {label}
      </p>
      <p className={cn('text-2xl font-bold', colorClass)}>{value}</p>
    </Card>
  );
}

function NodeCard({ node }: { node: RuntimeNode }) {
  return (
    <Card className="p-5">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className={cn('w-2.5 h-2.5 rounded-full shrink-0', STATUS_DOT[node.status])}
            aria-hidden="true"
          />
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-raios-text truncate">
              {node.hostname}
            </h3>
            {node.tailscale_ip && (
              <p className="text-[11px] font-mono text-raios-muted truncate">
                {node.tailscale_ip}
              </p>
            )}
          </div>
        </div>
        <Badge variant={STATUS_BADGE[node.status]}>{node.status}</Badge>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <MetricPill
          icon={Cpu}
          label="RAM"
          value={`${node.ram_gb}GB`}
        />
        <MetricPill
          label="Workers"
          value={String(node.max_workers)}
        />
        <MetricPill
          label="In progress"
          value={String(node.tasks_in_progress ?? 0)}
        />
        <MetricPill
          label="Last heartbeat"
          value={relativeTime(node.last_heartbeat_at)}
        />
      </div>

      {/* Mini bar for in-progress vs capacity */}
      {node.max_workers > 0 && (
        <div className="mt-3">
          <div className="flex items-center justify-between text-[10px] text-raios-muted mb-1">
            <span>Uso</span>
            <span>
              {node.tasks_in_progress ?? 0}/{node.max_workers}
            </span>
          </div>
          <div className="h-1.5 bg-raios-elevated rounded-full overflow-hidden">
            <div
              className="h-full bg-raios-accent transition-all"
              style={{
                width: `${Math.min(
                  100,
                  ((node.tasks_in_progress ?? 0) / node.max_workers) * 100
                )}%`,
              }}
            />
          </div>
        </div>
      )}
    </Card>
  );
}

function MetricPill({
  icon: Icon,
  label,
  value,
}: {
  icon?: React.ElementType;
  label: string;
  value: string;
}) {
  return (
    <div className="bg-raios-elevated/30 px-3 py-2 rounded border border-raios-border">
      <div className="flex items-center gap-1.5 mb-0.5">
        {Icon && <Icon className="w-3 h-3 text-raios-muted" />}
        <span className="text-[10px] uppercase font-semibold text-raios-muted tracking-wider">
          {label}
        </span>
      </div>
      <span className="text-xs text-raios-text font-mono">{value}</span>
    </div>
  );
}

function SessionsChart({ sessions }: { sessions: RuntimeAccountSession[] }) {
  const max = Math.max(1, ...sessions.map((s) => s.active_sessions));
  return (
    <Card className="p-5">
      <ul className="space-y-3">
        {sessions.map((s) => (
          <li key={s.account_id}>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-raios-text font-mono truncate">
                {s.account_label ?? s.account_id}
              </span>
              <span className="text-raios-muted shrink-0 ml-2">
                {s.active_sessions}
              </span>
            </div>
            <div className="h-2 bg-raios-elevated rounded-full overflow-hidden">
              <div
                className="h-full bg-raios-accent"
                style={{ width: `${(s.active_sessions / max) * 100}%` }}
              />
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/* ─── Mock data (used only when endpoint returns 404) ──────────────────────── */

function buildMockData(): RuntimeNodesResponse {
  const now = new Date();
  const iso = (minsAgo: number) =>
    new Date(now.getTime() - minsAgo * 60_000).toISOString();
  return {
    nodes: [
      {
        id: 'node-mac-mini-m4',
        hostname: 'mac-mini-de-breno',
        tailscale_ip: '100.64.12.30',
        ram_gb: 16,
        max_workers: 10,
        status: 'online',
        last_heartbeat_at: iso(0.5),
        tasks_in_progress: 4,
        llm_sessions_active: 4,
        memory_pressure_events_24h: 1,
      },
      {
        id: 'node-air',
        hostname: 'brenos-macbook-air',
        tailscale_ip: '100.64.12.31',
        ram_gb: 8,
        max_workers: 3,
        status: 'draining',
        last_heartbeat_at: iso(2),
        tasks_in_progress: 1,
        llm_sessions_active: 1,
        memory_pressure_events_24h: 0,
      },
    ],
    llm_sessions_by_account: [
      { account_id: 'quaternary-max', account_label: 'quaternary-max', active_sessions: 3 },
      { account_id: 'primary-max', account_label: 'primary-max', active_sessions: 0 },
    ],
    memory_pressure_events: [
      {
        id: 'mp-1',
        node_id: 'node-mac-mini-m4',
        hostname: 'mac-mini-de-breno',
        level: 'warning',
        message: 'RSS 1.6GB (threshold 1.5GB)',
        occurred_at: iso(45),
      },
    ],
  };
}
